import React, { useState, useEffect } from 'react';
import { BoardMember, PendingBoardInvitation, boardMemberApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Group, Xmark } from 'iconoir-react';

interface BoardMembersProps {
  boardId: number;
  isOpen: boolean;
  onClose: () => void;
  userRole: string;
  boardOwnerId?: number;
}

export const BoardMembers: React.FC<BoardMembersProps> = ({ boardId, isOpen, onClose, userRole, boardOwnerId }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<BoardMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingBoardInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'visitor'>('visitor');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [linkRole, setLinkRole] = useState<'admin' | 'editor' | 'visitor'>('visitor');
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);

  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (isOpen) {
      loadMembers();
      if (isAdmin) {
        loadPendingInvitations();
      }
    }
  }, [isOpen, boardId, isAdmin]);

  const loadMembers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await boardMemberApi.getAll(boardId);
      setMembers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des membres');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPendingInvitations = async () => {
    try {
      const response = await boardMemberApi.getInvitations(boardId);
      setPendingInvitations(response.data);
    } catch {
      // Silencieux : l'admin verra juste 0 invitations le temps que ça revienne.
    }
  };

  const handleCancelInvitation = async (invitationId: number) => {
    if (!window.confirm('Annuler cette invitation ?')) return;
    try {
      await boardMemberApi.cancelInvitation(boardId, invitationId);
      await loadPendingInvitations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'annulation');
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    setError(null);
    try {
      await boardMemberApi.invite(boardId, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      setInviteRole('visitor');
      await loadMembers();
      await loadPendingInvitations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (memberId: number, newRole: 'admin' | 'editor' | 'visitor') => {
    try {
      await boardMemberApi.updateRole(boardId, memberId, newRole);
      await loadMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la modification du rôle');
    }
  };

  const handleGenerateLink = async () => {
    try {
      const response = await boardMemberApi.generateInviteLink(boardId, linkRole);
      setInviteLink(response.data.invite_url);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la génération du lien');
    }
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    
    try {
      await navigator.clipboard.writeText(inviteLink);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 2000);
    } catch (err) {
      setError('Erreur lors de la copie du lien');
    }
  };

  const handleDisableLink = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver le lien d\'invitation ?')) return;
    
    try {
      await boardMemberApi.disableInviteLink(boardId);
      setInviteLink(null);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la désactivation du lien');
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce membre ?')) return;

    try {
      await boardMemberApi.remove(boardId, memberId);
      await loadMembers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression du membre');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'role-badge role-admin';
      case 'editor':
        return 'role-badge role-editor';
      case 'visitor':
        return 'role-badge role-visitor';
      default:
        return 'role-badge';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrateur';
      case 'editor':
        return 'Éditeur';
      case 'visitor':
        return 'Visiteur';
      default:
        return role;
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container members-modal">
        <button className="modal-close" onClick={onClose}>
          <Xmark width={20} height={20} strokeWidth={2} />
        </button>
        
        <h2 className="modal-title">
          <span className="modal-title-icon">
            <Group width={24} height={24} strokeWidth={2} />
          </span>
          Membres du tableau
        </h2>

        {error && (
          <div className="error-message" style={{ marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Formulaire d'invitation (admin uniquement) */}
        {isAdmin && (
          <div className="invite-section">
            <h3>Inviter un membre</h3>
            <form onSubmit={handleInvite} className="invite-form">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Email de l'utilisateur"
                className="invite-input"
                disabled={isInviting}
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'admin' | 'editor' | 'visitor')}
                className="invite-select"
                disabled={isInviting}
              >
                <option value="visitor">Visiteur</option>
                <option value="editor">Éditeur</option>
                <option value="admin">Administrateur</option>
              </select>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isInviting || !inviteEmail.trim()}
              >
                {isInviting ? 'Invitation...' : 'Inviter'}
              </button>
            </form>

            {/* Section lien d'invitation partageable */}
            <div className="invite-link-section" style={{ marginTop: '20px' }}>
              <h4>Ou partagez un lien d'invitation</h4>
              <div className="invite-link-controls">
                <select
                  value={linkRole}
                  onChange={(e) => setLinkRole(e.target.value as 'admin' | 'editor' | 'visitor')}
                  className="invite-select"
                  style={{ marginRight: '8px' }}
                >
                  <option value="visitor">Visiteur</option>
                  <option value="editor">Éditeur</option>
                  <option value="admin">Administrateur</option>
                </select>
                <button
                  onClick={handleGenerateLink}
                  className="btn btn-secondary"
                >
                  Générer le lien
                </button>
              </div>

              {inviteLink && (
                <div className="invite-link-display" style={{ marginTop: '12px' }}>
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="invite-link-input"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #DFE1E6',
                      marginBottom: '8px',
                      fontFamily: 'monospace',
                      fontSize: '12px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleCopyLink}
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                    >
                      {showCopiedFeedback ? '✓ Copié !' : 'Copier le lien'}
                    </button>
                    <button
                      onClick={handleDisableLink}
                      className="btn btn-cancel"
                    >
                      Désactiver
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invitations en attente (admin uniquement) */}
        {isAdmin && pendingInvitations.length > 0 && (
          <div className="members-list pending-invitations-section">
            <h3>Invitations en attente ({pendingInvitations.length})</h3>
            <div className="members-items">
              {pendingInvitations.map((inv) => (
                <div key={inv.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar" style={{ background: '#f5a623' }}>
                      {inv.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-details">
                      <div className="member-name">{inv.email}</div>
                      <div className="member-email">
                        Invité par {inv.invited_by ?? 'utilisateur supprimé'} ·{' '}
                        {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </div>
                  <div className="member-actions">
                    <span className={getRoleBadgeClass(inv.role)}>{getRoleLabel(inv.role)}</span>
                    <button
                      className="btn btn-icon btn-danger"
                      onClick={() => handleCancelInvitation(inv.id)}
                      title="Annuler l'invitation"
                    >
                      <Xmark width={16} height={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liste des membres */}
        <div className="members-list">
          <h3>Membres ({members.length})</h3>
          {isLoading ? (
            <div className="loading-message">Chargement des membres...</div>
          ) : members.length === 0 ? (
            <div className="empty-message">Aucun membre</div>
          ) : (
            <div className="members-items">
              {members.map((member) => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-details">
                      <div className="member-name">
                        {member.name}
                        {member.id === user?.id && <span className="member-you"> (vous)</span>}
                      </div>
                      <div className="member-email">{member.email}</div>
                    </div>
                  </div>
                  <div className="member-actions">
                    {isAdmin ? (
                      <>
                        {member.id === boardOwnerId ? (
                          // Le propriétaire ne peut pas changer de rôle
                          <span className={getRoleBadgeClass(member.role)} style={{ marginRight: '8px' }}>
                            {getRoleLabel(member.role)} (Propriétaire)
                          </span>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value as 'admin' | 'editor' | 'visitor')}
                            className="member-role-select"
                          >
                            <option value="visitor">Visiteur</option>
                            <option value="editor">Éditeur</option>
                            <option value="admin">Administrateur</option>
                          </select>
                        )}
                        {member.id !== user?.id && member.id !== boardOwnerId && (
                          <button 
                            className="btn-remove-member"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Retirer ce membre"
                          >
                            <Xmark width={16} height={16} strokeWidth={2} />
                          </button>
                        )}
                      </>
                    ) : (
                      <span className={getRoleBadgeClass(member.role)}>
                        {getRoleLabel(member.role)}
                        {member.id === boardOwnerId && ' (Propriétaire)'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
